import { useCallback, useEffect, useRef, useState } from 'react';
import { readSpeechSettings, requestSpeech } from '../lib/managedSpeech';

export function useManagedSpeech() {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const audioRef = useRef(null);
  const requestRef = useRef(null);

  const stop = useCallback(() => {
    requestRef.current?.abort();
    requestRef.current = null;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setStatus('idle');
  }, []);

  useEffect(() => {
    const onHidden = () => { if (document.hidden) stop(); };
    const onOtherSpeech = (event) => { if (event.detail !== audioRef.current) stop(); };
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('managed-speech-play', onOtherSpeech);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('managed-speech-play', onOtherSpeech);
      requestRef.current?.abort();
    };
  }, [stop]);

  useEffect(() => {
    const audio = audioRef.current;
    return () => audio?.pause();
  }, [result]);

  const generate = useCallback(async (text, settings = readSpeechSettings()) => {
    stop();
    setError('');
    setResult(null);
    if (!text.trim() || text.length > 4096) {
      setError('Use 1–4,096 characters per recording.');
      return;
    }
    const controller = new AbortController();
    requestRef.current = controller;
    setStatus('generating');
    try {
      const recording = await requestSpeech(text, settings, controller.signal);
      if (requestRef.current !== controller) return;
      setResult(recording);
      setStatus('ready');
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err.message || 'Speech generation failed. Please try again.');
      setStatus('idle');
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
    }
  }, [stop]);

  return { audioRef, error, generate, result, setError, setStatus, status, stop };
}